import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { getDefaultConfig, validateNotificationConfig } from '../constants/notificationTypes';

/**
 * School Notification Service
 * Manages school-wide notification settings and configurations
 */
class SchoolNotificationService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get school notification settings
   * @param {string} schoolId - School ID
   * @returns {Promise<object>} School notification settings
   */
  async getSchoolNotificationSettings(schoolId) {
    const cacheKey = `school_settings_${schoolId}`;
    const cached = this.cache.get(cacheKey);

    // Return cached data if fresh
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }

    try {
      const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
      
      if (!schoolDoc.exists()) {
        throw new Error('School not found');
      }

      const schoolData = schoolDoc.data();
      const defaults = this.getDefaultSchoolSettings();
      const saved = schoolData.notificationSettings || {};
      const settings = {
        ...defaults,
        ...saved,
        defaultChannels: { ...defaults.defaultChannels, ...(saved.defaultChannels || {}) },
      };

      // Cache the result
      this.cache.set(cacheKey, {
        data: settings,
        timestamp: Date.now()
      });

      return settings;
    } catch (error) {
      console.error('Error fetching school notification settings:', error);
      throw error;
    }
  }

  /**
   * Update school notification settings
   * @param {string} schoolId - School ID
   * @param {object} settings - Settings to update
   * @returns {Promise<object>} Updated settings
   */
  async updateSchoolNotificationSettings(schoolId, settings) {
    try {
      const schoolRef = doc(db, 'schools', schoolId);

      // Validate the settings
      const validationResult = this.validateSchoolSettings(settings);
      if (!validationResult.isValid) {
        throw new Error(`Invalid settings: ${validationResult.errors.join(', ')}`);
      }

      // Update the school document
      await updateDoc(schoolRef, {
        notificationSettings: settings,
        updatedAt: serverTimestamp()
      });

      // Clear cache
      this.cache.delete(`school_settings_${schoolId}`);

      return await this.getSchoolNotificationSettings(schoolId);
    } catch (error) {
      console.error('Error updating school notification settings:', error);
      throw error;
    }
  }

  /**
   * Update only the weekly-discipline-brief fields, scoped.
   *
   * The brief owns just the weeklyBrief* keys. Writing them via dotted field
   * paths leaves every sibling setting (defaultChannels, quiet hours, etc.)
   * untouched and — crucially — avoids re-validating unrelated config the brief
   * has no business touching. (Round-tripping the whole notificationSettings
   * blob fails validation whenever a sibling default is invalid, e.g. the
   * Emergencies default lists the disabled `sms` channel.)
   *
   * @param {string} schoolId
   * @param {object} fields - any of weeklyBriefEnabled/Day/Time/Recipients
   * @returns {Promise<object>} Refreshed settings
   */
  async updateWeeklyBriefSettings(schoolId, fields) {
    const ALLOWED = ['weeklyBriefEnabled', 'weeklyBriefDay', 'weeklyBriefTime', 'weeklyBriefRecipients'];
    const update = { updatedAt: serverTimestamp() };
    for (const key of ALLOWED) {
      if (key in fields) update[`notificationSettings.${key}`] = fields[key];
    }
    try {
      await updateDoc(doc(db, 'schools', schoolId), update);
      this.cache.delete(`school_settings_${schoolId}`);
      return await this.getSchoolNotificationSettings(schoolId);
    } catch (error) {
      console.error('Error updating weekly brief settings:', error);
      throw error;
    }
  }

  /**
   * Get default school notification settings
   * @returns {object} Default settings
   */
  getDefaultSchoolSettings() {
    return {
      // Global settings
      enabled: true,
      defaultTiming: 'immediate',
      
      // Batching settings
      batchingEnabled: false,
      batchHour: '15',
      batchMinute: '30',
      
      // Daily summary settings
      dailySummaryEnabled: false,
      dailySummaryTime: '17:00',
      dailySummaryChannels: ['telegram'],
      
      // Weekly summary settings
      weeklySummaryEnabled: false,
      weeklySummaryDay: 'friday',
      weeklySummaryTime: '16:00',
      weeklySummaryChannels: ['email'],
      
      // Quiet hours
      quietHoursEnabled: false,
      quietHoursStart: '21:00',
      quietHoursEnd: '07:00',
      quietHoursTimezone: 'America/New_York',
      
      // Default channels by notification type
      defaultChannels: {
        behavior: getDefaultConfig('behavior'),
        attendance: getDefaultConfig('attendance'),
        achievements: getDefaultConfig('achievements'),
        announcements: getDefaultConfig('announcements'),
        emergencies: getDefaultConfig('emergencies'),
        homework: getDefaultConfig('homework'),
        reminders: getDefaultConfig('reminders')
      }
    };
  }

  /**
   * Validate school notification settings
   * @param {object} settings - Settings to validate
   * @returns {object} Validation result
   */
  validateSchoolSettings(settings) {
    const errors = [];

    // Global settings validation
    if (typeof settings.enabled !== 'boolean') {
      errors.push('enabled must be a boolean');
    }

    if (!['immediate', 'batched', 'scheduled'].includes(settings.defaultTiming)) {
      errors.push('defaultTiming must be one of: immediate, batched, scheduled');
    }

    // Batching validation
    if (typeof settings.batchingEnabled !== 'boolean') {
      errors.push('batchingEnabled must be a boolean');
    }

    if (settings.batchingEnabled) {
      const batchHour = parseInt(settings.batchHour);
      if (isNaN(batchHour) || batchHour < 0 || batchHour > 23) {
        errors.push('batchHour must be between 0 and 23');
      }

      const batchMinute = parseInt(settings.batchMinute);
      if (isNaN(batchMinute) || ![0, 15, 30, 45].includes(batchMinute)) {
        errors.push('batchMinute must be one of: 0, 15, 30, 45');
      }
    }

    // Daily summary validation
    if (typeof settings.dailySummaryEnabled !== 'boolean') {
      errors.push('dailySummaryEnabled must be a boolean');
    }

    if (settings.dailySummaryEnabled) {
      if (!settings.dailySummaryTime || !/^\d{2}:\d{2}$/.test(settings.dailySummaryTime)) {
        errors.push('dailySummaryTime must be in HH:MM format');
      }

      if (!Array.isArray(settings.dailySummaryChannels) || settings.dailySummaryChannels.length === 0) {
        errors.push('dailySummaryChannels must be a non-empty array');
      }
    }

    // Weekly summary validation
    if (typeof settings.weeklySummaryEnabled !== 'boolean') {
      errors.push('weeklySummaryEnabled must be a boolean');
    }

    if (settings.weeklySummaryEnabled) {
      const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      if (!validDays.includes(settings.weeklySummaryDay)) {
        errors.push('weeklySummaryDay must be a valid day of the week');
      }

      if (!settings.weeklySummaryTime || !/^\d{2}:\d{2}$/.test(settings.weeklySummaryTime)) {
        errors.push('weeklySummaryTime must be in HH:MM format');
      }

      if (!Array.isArray(settings.weeklySummaryChannels) || settings.weeklySummaryChannels.length === 0) {
        errors.push('weeklySummaryChannels must be a non-empty array');
      }
    }

    // Quiet hours validation
    if (typeof settings.quietHoursEnabled !== 'boolean') {
      errors.push('quietHoursEnabled must be a boolean');
    }

    if (settings.quietHoursEnabled) {
      if (!settings.quietHoursStart || !/^\d{2}:\d{2}$/.test(settings.quietHoursStart)) {
        errors.push('quietHoursStart must be in HH:MM format');
      }

      if (!settings.quietHoursEnd || !/^\d{2}:\d{2}$/.test(settings.quietHoursEnd)) {
        errors.push('quietHoursEnd must be in HH:MM format');
      }

      if (!settings.quietHoursTimezone) {
        errors.push('quietHoursTimezone is required');
      }
    }

    // Default channels validation
    if (settings.defaultChannels) {
      Object.entries(settings.defaultChannels).forEach(([type, config]) => {
        const validation = validateNotificationConfig(config);
        if (!validation.isValid) {
          errors.push(`Invalid config for ${type}: ${validation.errors.join(', ')}`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get effective configuration for a notification type
   * Combines school defaults with user preferences
   * @param {string} schoolId - School ID
   * @param {string} userId - User ID
   * @param {string} notificationType - Notification type ID
   * @param {object} userPreferences - User notification preferences
   * @returns {Promise<object>} Effective configuration
   */
  async getEffectiveNotificationConfig(schoolId, userId, notificationType, userPreferences = {}) {
    try {
      // Get school settings
      const schoolSettings = await this.getSchoolNotificationSettings(schoolId);
      
      // Get default config for this type
      const schoolDefault = schoolSettings.defaultChannels?.[notificationType] || 
                           getDefaultConfig(notificationType, schoolSettings);

      // Apply user overrides (if allowed by school settings)
      const typeConfig = this.getNotificationType(notificationType);
      const userConfigurable = typeConfig?.userConfigurable || [];
      
      let effectiveConfig = { ...schoolDefault };

      // Apply user preferences for configurable fields
      if (userPreferences.channels && userConfigurable.includes('channels')) {
        effectiveConfig.channels = userPreferences.channels;
      }

      if (userPreferences.priority && userConfigurable.includes('priority')) {
        effectiveConfig.priority = userPreferences.priority;
      }

      if (userPreferences.timing && userConfigurable.includes('timing')) {
        effectiveConfig.timing = userPreferences.timing;
      }

      // Apply global user settings
      if (userPreferences.enabled === false) {
        effectiveConfig.enabled = false;
      }

      // Check quiet hours
      if (schoolSettings.quietHoursEnabled && this.isInQuietHours(schoolSettings)) {
        const priorityConfig = this.getPriorityConfig(effectiveConfig.priority);
        if (!priorityConfig?.bypassQuietHours) {
          effectiveConfig.deferred = true;
          effectiveConfig.deferredUntil = this.getNextNonQuietHour(schoolSettings);
        }
      }

      return effectiveConfig;
    } catch (error) {
      console.error('Error getting effective notification config:', error);
      // Return basic default if something goes wrong
      return getDefaultConfig(notificationType);
    }
  }

  /**
   * Check if current time is within quiet hours
   * @param {object} quietHours - Quiet hours configuration
   * @returns {boolean} True if currently in quiet hours
   */
  isInQuietHours(quietHours) {
    if (!quietHours.enabled) return false;

    const now = new Date();
    const currentTime = now.getHours().toString().padStart(2, '0') + ':' + 
                       now.getMinutes().toString().padStart(2, '0');

    const { start, end } = quietHours;

    // Handle overnight quiet hours (e.g., 21:00 to 07:00)
    if (start > end) {
      return currentTime >= start || currentTime <= end;
    } else {
      return currentTime >= start && currentTime <= end;
    }
  }

  /**
   * Get next non-quiet hour time
   * @param {object} quietHours - Quiet hours configuration
   * @returns {Date} Next time outside quiet hours
   */
  getNextNonQuietHour(quietHours) {
    const now = new Date();
    const endTime = quietHours.quietHoursEnd.split(':');
    
    const nextTime = new Date(now);
    nextTime.setHours(parseInt(endTime[0]), parseInt(endTime[1]), 0, 0);

    // If next time is earlier than now, it means it's tomorrow
    if (nextTime <= now) {
      nextTime.setDate(nextTime.getDate() + 1);
    }

    return nextTime;
  }

  /**
   * Get notification type configuration (helper method)
   * @param {string} typeId - Notification type ID
   * @returns {object} Type configuration
   */
  getNotificationType(typeId) {
    // This would import from the constants file
    // For now, return null - will be implemented
    return null;
  }

  /**
   * Get priority configuration (helper method)
   * @param {string} priorityId - Priority ID
   * @returns {object} Priority configuration
   */
  getPriorityConfig(priorityId) {
    // This would import from the constants file
    // For now, return null - will be implemented
    return null;
  }

  /**
   * Clear cache for a school
   * @param {string} schoolId - School ID
   */
  clearCache(schoolId) {
    this.cache.delete(`school_settings_${schoolId}`);
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    this.cache.clear();
  }
}

// Create singleton instance
const schoolNotificationService = new SchoolNotificationService();

export default schoolNotificationService;